import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Moment } from "moment";
import { useRouter } from "@miplatform/hooks";
import {
  CalendarEventType,
  HubStatuses,
  IAttempt,
  IAttemptListItemResponseForWorkOrder,
  IAttemptListResponse,
  IAttemptTrayConfig,
  ICalendarConfig,
  IDateRangeISO,
  ITenantHolidayResponse,
  IUserListItemResponse,
} from "@miplatform/types";
import { useSignalR } from "@mibuildweb/hooks";
import { loadCalendarEvents } from "./utils/loadCalendarEvents";
import { CalendarSetsConfigDataItem } from "../../stores/calendarsStore/calendarsStore.types";
import { HoldAttemptFormData } from "../../views/NewHomeConstruction/NhcCalendar.types";
import { useCalendarProviderViewModel } from "./useCalendarProviderViewModel";

const CalendarContext = createContext<CalendarContextType | undefined>(
  undefined
);

export const CalendarProvider: React.FC<CalendarProviderProps> = ({
  children,
}) => {
  const { params } = useRouter();
  const { calendarSetId, calendarId: initialCalendarId } = params;

  const [selectedUser, setSelectedUser] =
    useState<IUserListItemResponse | null>(null);

  const handleSelectUser = useCallback((user: IUserListItemResponse | null) => {
    setSelectedUser(user);
  }, []);

  const {
    events,
    zoom,
    assignableUsers,
    schedulerUsers,
    activeCalendarId,
    calendarSet,
    calendarConfig,
    date,
    dateRange,
    search,
    debouncedSearch,
    trayBuckets,
    allTrayEvents,
    tenantHolidays,
    users,
    loadUsers,
    isLoading: isCalendarLoading,
    selectedAttempt,
    setCalendarDate,
    onDateRangeChange: setDateRange,
    setZoom,
    clearWorkOrderSchedule,
    unscheduleAttempt,
    handleLoadEvents,
    onAttemptsUpdate,
    handleCalendarSearch,
    getTrayDataForCalendar,
    getTenantHolidays,
    resetTrays,
    calendarCounts,
    resetSearch,
    setSelectedAttempt,
    setVerticalTabActiveFilter,
    verticalTabActiveFilter,
    holdWorkOrder,
  } = useCalendarProviderViewModel({
    calendarSetId,
    calendarId: initialCalendarId,
    selectedUser,
  });

  const assignableUserIdsKey = useMemo(
    () =>
      (assignableUsers ?? [])
        .map((user: IUserListItemResponse) => user.id)
        .join(","),
    [assignableUsers]
  );

  const assignableUserIds = useMemo(
    () => (assignableUserIdsKey ? assignableUserIdsKey.split(",") : []),
    [assignableUserIdsKey]
  );

  const {
    isLoading: isSignalRLoading,
    connectionStatus,
    start,
    stop,
  } = useSignalR({
    calendarSetId: calendarSetId ?? null,
    assignableUserIds,
    onAttemptsUpdate,
  });

  const eventsList = Object.values(events);

  const value: CalendarContextType = useMemo(
    () => ({
      zoom,
      date,
      dateRange,
      events: eventsList,
      calendarSet: calendarSet ?? null,
      calendarConfig,
      activeCalendarId,
      assignableUsers,
      schedulerUsers,
      connectionStatus,
      isCalendarLoading: isCalendarLoading || isSignalRLoading,
      search,
      debouncedSearch,
      selectedUser,
      trayBuckets,
      allTrayEvents,
      selectedAttempt,
      calendarCounts,
      tenantHolidays,
      users,
      loadUsers,
      clearWorkOrderSchedule,
      holdWorkOrder,
      unscheduleAttempt,
      setSelectedAttempt,
      setZoom,
      setCalendarDate,
      setDateRange,
      handleCalendarSearch,
      handleSelectUser,
      resetSearch,
      refreshData: handleLoadEvents,
      getTrayDataForCalendar,
      setVerticalTabActiveFilter,
      verticalTabActiveFilter,
    }),
    [
      zoom,
      calendarConfig,
      calendarSet,
      activeCalendarId,
      assignableUsers,
      schedulerUsers,
      isCalendarLoading,
      isSignalRLoading,
      connectionStatus,
      tenantHolidays,
      events,
      date,
      dateRange,
      search,
      debouncedSearch,
      selectedUser,
      trayBuckets,
      allTrayEvents,
      selectedAttempt,
      calendarCounts,
      verticalTabActiveFilter,
      users,
      resetSearch,
      holdWorkOrder,
      unscheduleAttempt,
      clearWorkOrderSchedule,
      loadUsers,
      loadCalendarEvents,
      handleSelectUser,
    ]
  );

  useEffect(() => {
    if (!calendarSetId) {
      return undefined;
    }
    start();
    // Always tear down on cleanup. Previously this was guarded by
    // `connectionStatus === Connected`, but `connectionStatus` was not part of
    // the dependency array, so the cleanup almost always saw the initial
    // `Disconnected` value and skipped stop(), leaking the SignalR hub and its
    // subscriptions across re-runs and on unmount.
    return () => {
      stop();
    };
  }, [calendarSetId, assignableUserIdsKey, start, stop]);

  useEffect(() => {
    handleLoadEvents();
  }, [
    calendarConfig,
    calendarSetId,
    date,
    debouncedSearch,
    dateRange,
    selectedUser,
  ]);

  useEffect(() => {
    loadUsers();
  }, [calendarConfig, date, selectedUser, loadUsers]);

  useEffect(() => {
    getTenantHolidays();
  }, []);

  useEffect(() => {
    // TODO: Remove this logic check wehn trays are implemented nicely for other calendar types
    if (calendarSet?.config.eventType === CalendarEventType.Order) {
      getTrayDataForCalendar();
    }

    return () => {
      resetTrays();
    };
  }, [calendarSet?.config.id]);

  // TODO: Refreshing trays re-renders everything and that will include losing the tooltips and actions while using the calendar. Something like this should be within the trays directly if possible
  // useEffect(() => {
  //   if (calendarSet) {
  //     timerRef.current = setInterval(() => {
  //       getTrayDataForCalendar();
  //     }, getTotalMilliseconds(calendarSet.config.pollingPeriodSeconds));
  //   }

  //   return () => {
  //     if (timerRef.current) {
  //       clearInterval(timerRef.current);
  //       timerRef.current = null;
  //     }
  //     resetTrays();
  //   };
  // }, [calendarSet?.config.id]);

  return (
    <CalendarContext.Provider value={value}>
      {children}
    </CalendarContext.Provider>
  );
};

export const useCalendarContext = (): CalendarContextType => {
  const context = useContext(CalendarContext);
  if (context === undefined) {
    throw new Error("useCalendarContext must be used within CalendarProvider");
  }
  return context;
};

interface CalendarContextType {
  date: Moment;
  dateRange: IDateRangeISO;
  setCalendarDate: (newDate: Date | string | Moment) => void;
  setDateRange: (newRange: IDateRangeISO) => void;
  zoom: number;
  setZoom: (zoom: number) => void;
  events: IAttempt[];
  activeCalendarId?: string;
  calendarCounts: Record<string, number>;
  calendarSet: CalendarSetsConfigDataItem | null;
  calendarConfig: ICalendarConfig | null;
  assignableUsers?: IUserListItemResponse[];
  schedulerUsers?: IUserListItemResponse[];
  connectionStatus: HubStatuses;
  isCalendarLoading: boolean;
  search: string;
  debouncedSearch: string;
  users: IUserListItemResponse[];
  tenantHolidays: ITenantHolidayResponse[];
  loadUsers: () => void;
  handleCalendarSearch: (value: string) => void;
  handleSelectUser: (user: IUserListItemResponse | null) => void;
  selectedUser: IUserListItemResponse | null;
  refreshData: () => void;
  trayBuckets: IAttemptTrayConfig[];
  allTrayEvents:
    | IAttemptListResponse[]
    | IAttemptListItemResponseForWorkOrder[];
  selectedAttempt: IAttempt | null;
  holdWorkOrder: (data: HoldAttemptFormData) => Promise<Response>;
  setSelectedAttempt: (attempt: IAttempt | null) => void;
  resetSearch: () => void;
  clearWorkOrderSchedule: (user: IUserListItemResponse) => void;
  getTrayDataForCalendar: () => Promise<void>;
  setVerticalTabActiveFilter: (filter: string | null) => void;
  unscheduleAttempt: (attempt: IAttemptListItemResponseForWorkOrder) => void;
  verticalTabActiveFilter: string | null;
}
interface CalendarProviderProps {
  children: ReactNode;
}
